import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiUrl } from "@/lib/api";
import { PageHeader, Section, Btn, Select } from "@/components/Shared";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { Download, RefreshCw, ArrowUpRight, ArrowDownRight, Minus, Info, ArrowUpRight as ExternalArrow } from "lucide-react";
import { fmtINR, fmtCompact, fmtPeriod, periodOptions } from "@/lib/api";
import { toast } from "sonner";

const COLORS = ["#2563EB", "#0F172A", "#64748B", "#0891B2", "#7C3AED", "#059669"];
// Distinct hue per lead source so the donut + legend are instantly readable.
const LEAD_SOURCE_COLORS = { admin: "#0F172A", partner: "#2563EB", merchant: "#0891B2" };

// Compact inline SVG sparkline (no axes, no padding) for PG trend cards.
function Sparkline({ values, stroke = "#2563EB", fill = "#DBEAFE", width = 140, height = 36 }) {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const n = values.length;
  if (n === 0) return null;
  const step = n > 1 ? width / (n - 1) : width;
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const path = `M ${pts.join(" L ")}`;
  const area = `${path} L ${width},${height} L 0,${height} Z`;
  return (
    <svg width={width} height={height} className="block">
      <path d={area} fill={fill} opacity="0.5" />
      <path d={path} stroke={stroke} strokeWidth="1.8" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      {values.map((v, i) => v > 0 && i === n - 1 ? (
        <circle key={i} cx={i * step} cy={height - ((v - min) / range) * (height - 4) - 2} r="2.5" fill={stroke} />
      ) : null)}
    </svg>
  );
}

function PgTrendCard({ g, testid }) {
  const dir = g.delta_pct === null ? "flat" : (g.delta_pct > 0 ? "up" : g.delta_pct < 0 ? "down" : "flat");
  const Icon = dir === "up" ? ArrowUpRight : dir === "down" ? ArrowDownRight : Minus;
  const color = dir === "up" ? "text-[var(--positive)]" : dir === "down" ? "text-[var(--negative)]" : "text-[var(--muted)]";
  const sparkColor = dir === "up" ? "#059669" : dir === "down" ? "#DC2626" : "#64748B";
  const sparkFill = dir === "up" ? "#D1FAE5" : dir === "down" ? "#FEE2E2" : "#E5E7EB";
  return (
    <div className="bg-[var(--surface)] border border-[var(--line)] p-4 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{g.gateway}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--muted)] mt-0.5">{fmtPeriod(g.latest_ym) || "No data"}</div>
        </div>
        {g.delta_pct !== null ? (
          <div className={`flex items-center gap-0.5 text-[11px] font-semibold ${color} shrink-0`}>
            <Icon size={12} strokeWidth={2.4} />
            <span className="num">{(Math.abs(g.delta_pct) * 100).toFixed(1)}%</span>
          </div>
        ) : (
          <div className="text-[10px] text-[var(--muted)] font-medium shrink-0">new</div>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="num text-lg font-semibold text-[var(--ink)]">₹ {fmtCompact(g.latest_val)}</div>
        <Sparkline values={g.series} stroke={sparkColor} fill={sparkFill} width={100} height={32} />
      </div>
      <div className="text-[10.5px] text-[var(--muted)] border-t border-[var(--line)] pt-1.5 flex justify-between">
        <span>Prior: <span className="num">₹ {fmtCompact(g.prior_val)}</span> {g.prior_ym && <span className="text-[var(--muted)]/80">({fmtPeriod(g.prior_ym)})</span>}</span>
        <span>12-mo: <span className="num">₹ {fmtCompact(g.total)}</span></span>
      </div>
    </div>
  );
}

function KpiWithDelta({ label, value, sub, delta, latestPeriod, priorPeriod, accent, testid, tooltip, to }) {
  const dir = delta?.direction || "flat";
  const Icon = dir === "up" ? ArrowUpRight : dir === "down" ? ArrowDownRight : Minus;
  const color = dir === "up" ? "text-[var(--positive)]" : dir === "down" ? "text-[var(--negative)]" : "text-[var(--muted)]";
  const showDelta = priorPeriod && delta;
  const Wrapper = to ? Link : "div";
  const wrapperProps = to
    ? { to, "data-testid": testid, className: "rise bg-[var(--surface)] border border-[var(--line)] p-6 block group hover:border-[var(--ink)] hover:shadow-sm transition-all cursor-pointer relative" }
    : { "data-testid": testid, className: "rise bg-[var(--surface)] border border-[var(--line)] p-6" };
  return (
    <Wrapper {...wrapperProps}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
        {label}
        {tooltip && (
          <span className="relative inline-flex group/tip" data-testid={`${testid}-info-wrap`}>
            <span
              className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[var(--muted)] hover:text-[var(--accent)] cursor-help"
              data-testid={`${testid}-info`}
              aria-label={`About ${label}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <Info size={12} strokeWidth={2} />
            </span>
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 z-20 w-64 px-3 py-2 bg-[var(--ink)] text-white text-[11px] font-normal normal-case tracking-normal leading-snug rounded-sm shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150"
            >
              {tooltip}
            </span>
          </span>
        )}
        {to && (
          <ExternalArrow size={11} strokeWidth={2.4} className="ml-auto text-[var(--muted)] opacity-0 group-hover:opacity-100 group-hover:text-[var(--accent)] transition-opacity" />
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className={`num text-2xl xl:text-3xl font-semibold whitespace-nowrap ${accent ? "text-[var(--accent)]" : "text-[var(--ink)]"}`}>{value}</div>
      </div>
      <div className="text-[11px] text-[var(--muted)] mt-1.5">{sub}</div>
      {showDelta && (
        <div className={`flex items-center gap-1 mt-3 text-[12px] font-medium ${color}`}>
          <Icon size={13} strokeWidth={2.2} />
          <span className="num">{delta.pct === null ? "new" : `${(delta.pct * 100).toFixed(1)}%`}</span>
          <span className="text-[var(--muted)] font-normal text-[11px] ml-1">vs {fmtPeriod(priorPeriod)} ({fmtPeriod(latestPeriod)})</span>
        </div>
      )}
    </Wrapper>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [leadsStats, setLeadsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("");

  const load = async (p = period) => {
    setLoading(true);
    try {
      const [dashRes, trendRes, leadsRes] = await Promise.all([
        api.get(`/dashboard${p ? `?period=${p}` : ""}`),
        api.get(`/pg-earnings/trend?months=12`),
        api.get(`/leads/stats/by-source?days=90`).catch(() => ({ data: null })),
      ]);
      setData(dashRes.data);
      setTrend(trendRes.data);
      setLeadsStats(leadsRes.data);
    } catch (e) { toast.error("Failed to load summary"); }
    setLoading(false);
  };

  useEffect(() => { load(""); }, []);
  const onPeriod = (v) => { setPeriod(v); load(v); };
  const exportCSV = () => window.open(apiUrl("/export/dashboard"), "_blank");

  // Phase 1 — memoized filtered chart inputs to avoid re-running the filter
  // pass on every render of the dashboard. Defined BEFORE the early return
  // so React hook ordering stays consistent across renders (rules-of-hooks).
  const lobChartData = useMemo(
    () => (data?.by_lob || []).filter((d) => d.net > 0),
    [data?.by_lob],
  );
  const leadsSourceChartData = useMemo(
    () => (leadsStats?.by_source || []).filter((d) => d.count > 0),
    [leadsStats],
  );

  if (loading || !data) return <div className="p-4 sm:p-10 text-[var(--muted)]" data-testid="dashboard-loading">Loading executive summary…</div>;

  const k = data.kpis;
  const kl = data.kpis_latest;
  const d = data.deltas;
  const lp = data.latest_period;
  const pp = data.prior_period;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Summary"
        actions={
          <>
            <div className="w-44">
              <Select
                testid="summary-period"
                placeholder="Latest period"
                options={[
                  { value: "all", label: "All periods (cumulative)" },
                  ...periodOptions(data.available_periods || []),
                ]}
                value={period}
                onChange={onPeriod}
              />
            </div>
            <Btn variant="ghost" testid="refresh-dashboard" onClick={() => load(period)}><RefreshCw size={14} /> Refresh</Btn>
            <Btn variant="accent" testid="export-dashboard" onClick={exportCSV}><Download size={14} /> Export CSV</Btn>
          </>
        }
      />

      <div className="p-4 sm:p-10 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 auto-rows-fr">
          {period === "all" ? (
            <>
              <KpiWithDelta testid="kpi-gross" to="/internal-margin" label="Gross Commission" value={`₹ ${fmtCompact(k.gross)}`} sub={`Cumulative across ${(data.available_periods || []).length} period${(data.available_periods || []).length === 1 ? "" : "s"}`} />
              <KpiWithDelta testid="kpi-expenses" to="/monthly-accounting" label="Total Expenses" value={`₹ ${fmtCompact(k.total_expense || 0)}`} sub="Cumulative · all periods" accent tooltip="= Partner Payouts + Operational Expenses + Other Expenses. Pulled live from Monthly Accounting." />
              <KpiWithDelta testid="kpi-net" to="/monthly-accounting" label="BizAssist's Net Revenue" value={`₹ ${fmtCompact(k.net)}`} sub="Cumulative · all periods" tooltip="= Gross Commission − Total Expenses" />
              <KpiWithDelta testid="kpi-volume" to="/volume" label="Total Volume" value={`₹ ${fmtCompact(k.volume)}`} sub={`${k.programs} merchants · ${k.partners} partners`} />
            </>
          ) : (
            <>
              <KpiWithDelta testid="kpi-gross" to="/internal-margin" label="Gross Commission" value={`₹ ${fmtCompact(kl.gross)}`} sub={`All-time: ₹ ${fmtCompact(k.gross)}`} delta={d.gross} latestPeriod={lp} priorPeriod={pp} />
              <KpiWithDelta testid="kpi-expenses" to="/monthly-accounting" label="Total Expenses" value={`₹ ${fmtCompact(kl.total_expense || 0)}`} sub={`All-time: ₹ ${fmtCompact(k.total_expense || 0)}`} delta={d.total_expense} latestPeriod={lp} priorPeriod={pp} accent tooltip="= Partner Payouts + Operational Expenses + Other Expenses. Pulled live from Monthly Accounting." />
              <KpiWithDelta testid="kpi-net" to="/monthly-accounting" label="BizAssist's Net Revenue" value={`₹ ${fmtCompact(kl.net)}`} sub={`All-time: ₹ ${fmtCompact(k.net)}`} delta={d.net} latestPeriod={lp} priorPeriod={pp} tooltip="= Gross Commission − Total Expenses" />
              <KpiWithDelta testid="kpi-volume" to="/volume" label="Total Volume" value={`₹ ${fmtCompact(kl.volume)}`} sub={`${k.programs} merchants · ${k.partners} partners`} delta={d.volume} latestPeriod={lp} priorPeriod={pp} />
            </>
          )}
        </div>

        {trend && trend.gateways && trend.gateways.length > 0 && (() => {
          // User-pinned the headline trend to the 3 PGs that drive ~80% of
          // commissions (Cashfree / Lyra / Rzp). Everything else lives one
          // click away on the dedicated /pg-earnings page so the Summary
          // page doesn't turn into a 10-card grid.
          const PRIMARY = ["Cashfree", "Lyra", "Rzp"];
          const primary = PRIMARY
            .map((name) => trend.gateways.find((g) => g.gateway === name))
            .filter(Boolean);
          // Fallback: if a configured gateway is missing from data, pad
          // with the next biggest by total — keeps the row from collapsing.
          const fallback = trend.gateways.filter((g) => !PRIMARY.includes(g.gateway));
          while (primary.length < 3 && fallback.length) primary.push(fallback.shift());
          const moreCount = trend.gateways.length - primary.length;
          return (
          <Section
            title="PG Earnings — Month-over-Month Trend (by commission earned)"
            testid="pg-trend-section"
            right={
              <div className="flex items-center gap-3 text-[11px]">
                <span className="text-[var(--muted)] uppercase tracking-[0.15em] font-semibold">Total {fmtPeriod(trend.grand_latest_ym)}</span>
                <span className="num text-[13px] font-semibold text-[var(--ink)]">₹ {fmtCompact(trend.grand_total_latest)}</span>
                {trend.grand_delta_pct !== null && (
                  <span className={`flex items-center gap-0.5 font-semibold ${trend.grand_delta_pct > 0 ? "text-[var(--positive)]" : trend.grand_delta_pct < 0 ? "text-[var(--negative)]" : "text-[var(--muted)]"}`}>
                    {trend.grand_delta_pct > 0 ? <ArrowUpRight size={12} strokeWidth={2.4} /> : <ArrowDownRight size={12} strokeWidth={2.4} />}
                    <span className="num">{(Math.abs(trend.grand_delta_pct) * 100).toFixed(1)}%</span>
                    <span className="text-[var(--muted)] font-normal ml-1">vs {fmtPeriod(trend.grand_prior_ym)}</span>
                  </span>
                )}
                <Link to="/pg-earnings" className="ml-2 text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-0.5" data-testid="pg-trend-view-all">
                  View all{moreCount > 0 ? ` (+${moreCount})` : ""} <ExternalArrow size={11} />
                </Link>
              </div>
            }
          >
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {primary.map((g) => (
                <PgTrendCard key={g.gateway} g={g} testid={`pg-trend-${g.gateway.replace(/\s+/g, '-').toLowerCase()}`} />
              ))}
            </div>
          </Section>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Section title="Gross Commission by Gateway" testid="chart-gateway">
            <div className="p-4 h-[280px]">
              <ResponsiveContainer>
                <BarChart data={data.by_gateway}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" />
                  <XAxis dataKey="gateway" stroke="#6B7280" fontSize={11} />
                  <YAxis stroke="#6B7280" fontSize={11} tickFormatter={fmtCompact} />
                  <Tooltip formatter={(v) => `₹ ${fmtINR(v, 0)}`} contentStyle={{ background: "#0D0F14", border: "none", color: "#fff", fontSize: 12 }} itemStyle={{ color: "#fff" }} labelStyle={{ color: "#fff" }} />
                  <Bar dataKey="gross" fill="#2563EB" radius={[2, 2, 0, 0]} name="Gross Commission" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section title="Net Commission by LOB" testid="chart-lob">
            <div className="p-4 h-[280px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={lobChartData} dataKey="net" nameKey="lob" outerRadius={90} innerRadius={45}>
                    {lobChartData.map((row, i) => <Cell key={row.lob || `lob-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `₹ ${fmtINR(v, 0)}`} contentStyle={{ background: "#0D0F14", border: "none", color: "#fff", fontSize: 12 }} itemStyle={{ color: "#fff" }} labelStyle={{ color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Section>

          {/* Leads donut — submissions in the last 90 days, bucketed by source.
              Always rendered (even when total=0) so the 3-card row stays balanced. */}
          <Section
            title="Lead submissions · last 90 days"
            testid="chart-leads-source"
            right={<Link to="/leads" className="text-[11.5px] text-[var(--accent)] hover:underline">Open →</Link>}
          >
            <div className="p-4 h-[280px]">
              {leadsStats && leadsStats.total > 0 ? (
                <div className="h-full grid grid-cols-[1fr_120px] gap-3 items-center">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={leadsSourceChartData}
                        dataKey="count" nameKey="label"
                        outerRadius={80} innerRadius={45}
                        paddingAngle={2}
                      >
                        {leadsSourceChartData.map((row) => (
                          <Cell key={row.source} fill={LEAD_SOURCE_COLORS[row.source] || "#94A3B8"} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, name) => [`${v} lead${v === 1 ? "" : "s"}`, name]}
                        contentStyle={{ background: "#0D0F14", border: "none", color: "#fff", fontSize: 12 }}
                        itemStyle={{ color: "#fff" }} labelStyle={{ color: "#fff" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5" data-testid="leads-source-legend">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total</div>
                    <div className="num text-2xl font-semibold text-[var(--ink)] leading-none">{leadsStats.total}</div>
                    <div className="text-[10px] text-[var(--muted)] mb-2">{leadsStats.days}d</div>
                    <div className="pt-2 border-t border-[var(--line)] space-y-1.5">
                      {leadsStats.by_source.map((row) => {
                        const pct = leadsStats.total > 0 ? Math.round((row.count / leadsStats.total) * 100) : 0;
                        return (
                          <div key={row.source} className="flex items-center gap-1.5 text-[11.5px]">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LEAD_SOURCE_COLORS[row.source] || "#94A3B8" }} />
                            <span className="text-[var(--ink-2)] flex-1 truncate">{row.label}</span>
                            <span className="num text-[var(--ink)] font-semibold">{row.count}</span>
                            <span className="num text-[var(--muted)] text-[10px] w-7 text-right">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center text-[var(--muted)]">
                  <div className="text-[12px]">No lead submissions in the last 90 days.</div>
                  <Link to="/leads" className="mt-2 text-[11.5px] text-[var(--accent)] hover:underline">Add a lead →</Link>
                </div>
              )}
            </div>
          </Section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Top Merchants by Gross Commission" testid="table-merchants">
            <div className="overflow-auto max-h-[360px]">
              {(() => {
                const totalVol = data.by_merchant.reduce((s, m) => s + (m.volume || 0), 0) || 1;
                return (
                  <div className="overflow-x-auto"><table className="table-data w-full">
                    <thead><tr>
                      <th>Merchant</th>
                      <th className="text-right">Volume</th>
                      <th className="text-right">% of Vol</th>
                      <th className="text-right">Gross Commission</th>
                      <th className="text-right">Net Commission</th>
                    </tr></thead>
                    <tbody>
                      {data.by_merchant.slice(0, 12).map((m) => (
                        <tr key={m.merchant_id}>
                          <td><div className="font-medium text-[var(--ink)]">{m.merchant_name}</div><div className="text-[11px] text-[var(--muted)] num">{m.merchant_id}</div></td>
                          <td className="text-right num">₹ {fmtCompact(m.volume)}</td>
                          <td className="text-right num text-[12px] text-[var(--muted)]">{((m.volume / totalVol) * 100).toFixed(1)}%</td>
                          <td className="text-right num font-medium">₹ {fmtINR(m.gross, 0)}</td>
                          <td className="text-right num text-[var(--positive)]">₹ {fmtINR(m.net, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                );
              })()}
            </div>
          </Section>

          <Section title="LOB Breakdown" testid="table-lob">
            <div className="overflow-auto max-h-[360px]">
              {(() => {
                const totalVol = data.by_lob.reduce((s, l) => s + (l.volume || 0), 0) || 1;
                return (
                  <div className="overflow-x-auto"><table className="table-data w-full">
                    <thead><tr>
                      <th>Line of Business</th>
                      <th className="text-right">Rows</th>
                      <th className="text-right">Volume</th>
                      <th className="text-right">% of Vol</th>
                      <th className="text-right">Gross Commission</th>
                      <th className="text-right">Net Commission</th>
                    </tr></thead>
                    <tbody>
                      {data.by_lob.map((l) => (
                        <tr key={l.lob}>
                          <td className="font-medium">{l.lob}</td>
                          <td className="text-right num">{l.rows}</td>
                          <td className="text-right num">₹ {fmtCompact(l.volume)}</td>
                          <td className="text-right num text-[12px] text-[var(--muted)]">{((l.volume / totalVol) * 100).toFixed(1)}%</td>
                          <td className="text-right num">₹ {fmtINR(l.gross, 0)}</td>
                          <td className="text-right num text-[var(--positive)]">₹ {fmtINR(l.net, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                );
              })()}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
